import { useTranslate } from '@tolgee/react';
import { Space, Button, message } from 'antd';
import { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/css';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useLatest } from 'ahooks';
import Implement from './Implement';
import { useClearAll, useSelectedItems } from './state';
import { useDefaultItems } from './useListData';
import { StoreTypeContextProvider, ConfigContext } from './context';
import useQuickAdd from '@knowledge/knowledges/spu/labelManage/QuickAdd';

type ItemSelectV2Props = {
  onSelect: (items: ItemSelectV2.Item[]) => void;
  showAddToLabel?: boolean;
  skuSelectDisabled?: boolean;
  excludeSpuIds?: string[];
  tooltips?: string;
  inSupplementKnowledge?: boolean;
};

/**
 * 商品选择器
 * @description 用于选择商品，目前适用场景为商品知识
 * 全平台商品支持选择
 * 搜索模式：按店铺，商品标签，商品名称搜索
 * 批量添加：支持按sku货号批量添加
 */
function useItemSelectV2(props: ItemSelectV2Props) {
  const {
    onSelect,
    showAddToLabel = true,
    skuSelectDisabled = false,
    excludeSpuIds = [],
    tooltips,
    inSupplementKnowledge,
  } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const items = useSelectedItems();
  const itemsRef = useLatest(items);
  const setDefault = useDefaultItems();
  const clearAll = useClearAll();
  const [openQuickAdd, quickAddInstance] = useQuickAdd();

  const open = useCallback(
    (items: ItemSelectV2.DefaultItem[]) => {
      items.length && setDefault(items);
      setVisible(true);
    },
    [setDefault],
  );
  const close = useCallback(() => {
    clearAll();
    setVisible(false);
  }, [clearAll]);
  const onConfirm = useCallback(() => {
    const items = itemsRef.current.map((item) => {
      const { skuInfo, ...rest } = item;
      return {
        ...rest,
        skuInfo: skuInfo.filter((sku) => sku._checked),
      };
    });
    onSelect?.(items);
    close();
  }, [onSelect, itemsRef, close]);

  const addToLabel = useCallback(() => {
    const items = itemsRef.current.map((item) => {
      const { skuInfo, ...rest } = item;
      return {
        ...rest,
        skuInfo: skuInfo.filter((sku) => sku._checked),
      };
    });
    if (items?.length > 0) {
      openQuickAdd(items);
    } else {
      message.warning(t('knowledge.item.at.least'));
    }
  }, [openQuickAdd, itemsRef, t]);

  const instance = visible && (
    <JustRightHeightModal
      visible={visible}
      title={t('aide.选择商品')}
      width={1300}
      centered
      onCancel={close}
      footer={
        <Space size={16}>
          <Button onClick={() => close()}>{t('phrase.取消')}</Button>
          {showAddToLabel && <Button onClick={addToLabel}>{t('knowledge.addToLabel')}</Button>}
          <Button type="primary" onClick={onConfirm}>
            {t('department.确认选择')}
          </Button>
        </Space>
      }
      className={css`
        .ant-modal-body {
          padding: 0;
          margin: 0 !important;
        }
      `}
    >
      {quickAddInstance}
      <ConfigContext.Provider
        value={{
          skuSelectDisabled,
          excludeSpuIds,
          inSupplementKnowledge,
        }}
      >
        <Implement tooltips={tooltips} />
      </ConfigContext.Provider>
    </JustRightHeightModal>
  );

  return [open, instance] as const;
}

export type ItemSelectV2ComponentProps = {
  visible: boolean;
  onCancel: () => void;
  value: ItemSelectV2.Item[];
  onChange: (value: ItemSelectV2.Item[]) => void;
  storeType: 'LOCAL' | 'CROSS' | 'ALL';
  storeScopes: General.StoreScope[];
  internalItemId?: string[];
  skuDisabled?: boolean;
  initialStoreScopes?: General.StoreScope[];
  sideStoreEnable?: boolean;
  defaultFoldSku?: boolean;
  confirmRender?: (confirm: ReactElement, onConfirm: () => void) => ReactNode;
  tooltips?: string;
};
// 组件形式提供
export function ItemSelectV2(props: ItemSelectV2ComponentProps) {
  const {
    visible,
    onCancel,
    value,
    onChange,
    storeType,
    storeScopes,
    internalItemId,
    skuDisabled,
    initialStoreScopes,
    sideStoreEnable,
    defaultFoldSku,
    confirmRender = (confirm) => confirm,
    tooltips,
  } = props;
  const onChangeRef = useLatest(onChange);
  const { t } = useTranslate();
  const items = useSelectedItems();
  const itemsRef = useLatest(items);
  const setDefault = useDefaultItems();
  const clearAll = useClearAll();
  const close = useCallback(() => {
    clearAll();
    onCancel?.();
  }, [clearAll, onCancel]);
  const onConfirm = useCallback(() => {
    const items = itemsRef.current.map((item) => {
      const { skuInfo, ...rest } = item;
      return {
        ...rest,
        skuInfo: skuInfo.filter((sku) => sku._checked),
      };
    });
    onChangeRef?.current?.(items);
    close();
  }, [onChangeRef, itemsRef, close]);
  useEffect(() => {
    if (visible) {
      setDefault(
        value?.map((each) => {
          const { externalItemId, internalItemId, skuInfo } = each;
          return {
            spuId: externalItemId,
            internalItemId,
            skus: skuInfo?.map((e) => ({ skuId: e.skuId })),
          };
        }),
      );
    }
  }, [visible, value, setDefault]);

  const confirm = (
    <Button type="primary" onClick={onConfirm}>
      {t('department.确认选择')}
    </Button>
  );

  return (
    <JustRightHeightModal
      visible={visible}
      closable={false}
      width={1300}
      centered
      onCancel={close}
      footer={
        <Space size={16}>
          <Button onClick={close}>{t('phrase.取消')}</Button>
          {confirmRender(confirm, onConfirm)}
        </Space>
      }
      destroyOnClose
      className={css`
        .ant-modal-body {
          padding: 0;
          margin: 0 !important;
        }
      `}
    >
      <StoreTypeContextProvider
        value={{
          storeType,
          storeScopes,
          internalItemId,
          initialStoreScopes,
        }}
      >
        <Implement
          skuDisabled={skuDisabled}
          sideStoreEnable={sideStoreEnable}
          defaultFoldSku={defaultFoldSku}
          tooltips={tooltips}
        />
      </StoreTypeContextProvider>
    </JustRightHeightModal>
  );
}

export default useItemSelectV2;
